// @wc-ignore-file
/**
 * CR-229 S5: the transport of the CR "Jira issues" section (contract:
 * shared/types/jira-context.ts).
 *
 * - `jira-mappings`: the project's enabled mappings as a member may see them
 *   (members cannot read integration_contexts — owner/admin RLS).
 * - `jira-links`: the CR's links, the stale statuses refreshed by the server
 *   first (D6). The section renders the direct database read and lets this
 *   answer replace it (change-requests-hooks.ts).
 * - `jira-send`: create / update the parent issue. With `includeChildren`
 *   (S6) the Technical Plan tasks are synced as child issues after it.
 *   CR-295: the dialog shows no preview, so the frontend never asks for the
 *   server's `dryRun`.
 * - `jira-sync-children` (S6): sync the plan tasks under an existing parent.
 * - Read the error body's `code` FIRST: the HTTP status is not a discriminant
 *   (409 is `existing_links`, `send_in_progress` and `jira_not_configured`).
 */
import { invokeFunction } from "@/lib/functions";
import type {
  CrJiraIssueLink,
  JiraContextErrorBody,
  JiraExistingLinksBody,
  JiraLinksSuccessBody,
  JiraMappingOption,
  JiraMappingsSuccessBody,
  JiraSendRequest,
  JiraSendSuccessBody,
  JiraSyncChildrenSuccessBody,
} from "@shared/types/jira-context";

/** A route error code (never `existing_links`, which is an outcome), or `network_error`. */
export type JiraClientErrorCode = JiraContextErrorBody["code"] | "network_error";

export class JiraContextError extends Error {
  readonly code: JiraClientErrorCode;
  readonly retryAfterSeconds?: number;

  constructor(code: JiraClientErrorCode, retryAfterSeconds?: number) {
    super(code);
    this.name = "JiraContextError";
    this.code = code;
    this.retryAfterSeconds = retryAfterSeconds;
  }
}

// A Record over the code union: a new route code fails typecheck here until it
// is listed (a missing one would silently read as `jira_unavailable`).
const ROUTE_CODE_FLAGS = {
  invalid_request: true,
  forbidden: true,
  cr_not_found: true,
  mapping_not_found: true,
  mapping_disabled: true,
  issue_type_not_allowed: true,
  breakdown_unavailable: true,
  link_not_found: true,
  send_in_progress: true,
  jira_not_configured: true,
  jira_reconnect_required: true,
  jira_not_found: true,
  jira_rate_limited: true,
  jira_invalid_request: true,
  jira_unavailable: true,
} satisfies Record<JiraContextErrorBody["code"], true>;
const ROUTE_CODES: ReadonlySet<string> = new Set(Object.keys(ROUTE_CODE_FLAGS));

/**
 * Map an invokeFunction error to a stable code. The membership middleware
 * answers `{ error }` without a code, so its refusal is read from the status.
 */
export function toJiraContextError(error: { status?: number; body?: unknown }): JiraContextError {
  const body = error.body as Partial<JiraContextErrorBody> | null | undefined;
  if (body && typeof body.code === "string" && ROUTE_CODES.has(body.code)) {
    const retryAfter =
      typeof body.retryAfterSeconds === "number" && Number.isFinite(body.retryAfterSeconds)
        ? body.retryAfterSeconds
        : undefined;
    return new JiraContextError(body.code as JiraClientErrorCode, retryAfter);
  }
  if (error.status === undefined) return new JiraContextError("network_error");
  if (error.status === 401 || error.status === 403) return new JiraContextError("forbidden");
  if (error.status === 400) return new JiraContextError("invalid_request");
  return new JiraContextError("jira_unavailable");
}

/** The mappings answer: the enabled mappings and whether Jira is connected (CR-268). */
export interface JiraMappingsAnswer {
  mappings: JiraMappingOption[];
  connected: boolean;
}

/** The project's enabled Jira mappings ([] = nothing to send with) and its connection state. */
export async function fetchJiraMappings(containerId: string): Promise<JiraMappingsAnswer> {
  const { data, error } = await invokeFunction<JiraMappingsSuccessBody>("jira-mappings", {
    body: { containerId },
  });
  if (error || !data) throw toJiraContextError(error ?? {});
  // An api-server older than CR-268 sends no flag: treat it as connected.
  return { mappings: data.mappings, connected: data.connected !== false };
}

/** The CR's links after the server refreshed the stale statuses (D6). */
export async function fetchJiraLinks(request: {
  containerId: string;
  crId: string;
}): Promise<Pick<JiraLinksSuccessBody, "links" | "refreshed">> {
  const { data, error } = await invokeFunction<JiraLinksSuccessBody>("jira-links", {
    body: request,
  });
  if (error || !data) throw toJiraContextError(error ?? {});
  return { links: data.links, refreshed: data.refreshed };
}

/** What the dialog sends: the request without the server-only fields (actor, dryRun). */
export type JiraSendInput = Omit<JiraSendRequest, "actorId" | "dryRun">;

/** A create that wrote nothing because the CR already has parent issues (the 409 step). */
interface JiraSendExistingLinks {
  status: "existing_links";
  existing: CrJiraIssueLink[];
}

export type JiraSendOutcome =
  | Pick<JiraSendSuccessBody, "status" | "parentLink" | "warnings" | "children">
  | JiraSendExistingLinks;

/**
 * Create or update the parent issue. `existing_links` is returned, not thrown:
 * it is an expected outcome the dialog answers with Update / Create another.
 */
export async function sendToJira(request: JiraSendInput): Promise<JiraSendOutcome> {
  const { data, error } = await invokeFunction<JiraSendSuccessBody>("jira-send", {
    body: request,
  });
  if (error) {
    const body = error.body as Partial<JiraExistingLinksBody> | null | undefined;
    if (body?.code === "existing_links" && Array.isArray(body.existing)) {
      return { status: "existing_links", existing: body.existing };
    }
    throw toJiraContextError(error);
  }
  if (!data) throw new JiraContextError("jira_unavailable");
  return {
    status: data.status,
    parentLink: data.parentLink,
    warnings: data.warnings,
    children: data.children,
  };
}

/** Sync the Technical Plan tasks as child issues of an existing parent link (S6). */
export async function syncJiraChildren(request: {
  containerId: string;
  crId: string;
  parentLinkId: string;
}): Promise<JiraSyncChildrenSuccessBody["children"]> {
  const { data, error } = await invokeFunction<JiraSyncChildrenSuccessBody>("jira-sync-children", {
    body: request,
  });
  if (error || !data) throw toJiraContextError(error ?? {});
  return data.children;
}

// Same host rule as SITE_HOST_RE in backend/api-server/src/lib/jira/jira-sites.ts.
const JIRA_HOST_RE = /^[a-z0-9-]+\.(atlassian\.net|jira\.com)$/i;

/**
 * A Jira issue link the page may render as an href, or null: https on an
 * Atlassian Cloud site only, no credentials or port. The server builds the
 * URL from the mapping's validated site; this is the belt to those braces, so
 * a stored value can never become a `javascript:` link.
 */
export function safeJiraUrl(url: string | null | undefined): string | null {
  if (typeof url !== "string") return null;
  try {
    const parsed = new URL(url);
    if (parsed.protocol !== "https:") return null;
    if (parsed.username || parsed.password || parsed.port) return null;
    if (!JIRA_HOST_RE.test(parsed.hostname)) return null;
    return parsed.href;
  } catch {
    return null;
  }
}
